import React, { Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AdminProvider } from "./contexts/AdminContext";
import { CookieConsentProvider } from "./components/GDPRCookieConsent";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import lazyWithRetry from "./utils/lazyWithRetry";

// Global Components - Load immediately (small size)
import WhatsAppWidget from "./components/WhatsAppWidget";
import ScrollToTop from "./components/ScrollToTop";
import SeasonalThemeOverlay from "./components/SeasonalThemeOverlay";

// Layout Components - Load immediately (needed for layout)
import Header from "./components/Header";
import MeteoConseilBanner from "./components/MeteoConseilBanner";
import Footer from "./components/Footer";
import EnglishFooter from "./components/EnglishFooter";
import { isEnglishPath } from "./data/i18nMap";
import StickyMobileCTA from "./components/StickyMobileCTA";

// Legacy URL Redirects - Load immediately
import LegacyRedirects from "./components/LegacyRedirects";
import LangSync from "./components/LangSync";

// Critical pages - Load immediately for SEO
import Home from "./pages/Home";

// Loading spinner component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
  </div>
);

// Lazy load all other pages for code splitting
const Services = lazyWithRetry(() => import("./pages/Services"));
const ServiceDetail = lazyWithRetry(() => import("./pages/ServiceDetail"));
const Tarifs = lazyWithRetry(() => import("./pages/Tarifs"));
const Devis = lazyWithRetry(() => import("./pages/Devis"));
const Zone = lazyWithRetry(() => import("./pages/Zone"));
const LocationPage = lazyWithRetry(() => import("./pages/LocationPage"));
const ServiceLocationPage = lazyWithRetry(() => import("./pages/ServiceLocationPage"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const MentionsLegales = lazyWithRetry(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazyWithRetry(() => import("./pages/PolitiqueConfidentialite"));
const Cookies = lazyWithRetry(() => import("./pages/Cookies"));
const ConditionsUtilisation = lazyWithRetry(() => import("./pages/ConditionsUtilisation"));

// Blog Pages - Lazy load
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogArticle = lazyWithRetry(() => import("./pages/BlogArticle"));
const BlogReview = lazyWithRetry(() => import("./pages/BlogReview"));

// Referral Page - Lazy load
const Parrainage = lazyWithRetry(() => import("./pages/Parrainage"));

// Long-tail SEO Pages - Lazy load
const EntretienResidenceSecondaire = lazyWithRetry(() => import("./pages/EntretienResidenceSecondaire"));
const TerrainAbandonneVendee = lazyWithRetry(() => import("./pages/TerrainAbandonneVendee"));
const CreditImpotJardinage = lazyWithRetry(() => import("./pages/CreditImpotJardinage"));
const AvanceImmediatePage = lazyWithRetry(() => import("./pages/AvanceImmediatePage"));

// Problem-Solution Pages - Lazy load
const JardinEnFriche = lazyWithRetry(() => import("./pages/JardinEnFriche"));
const HaiesTropHautes = lazyWithRetry(() => import("./pages/HaiesTropHautes"));

// About Page - Lazy load
const MonParcours = lazyWithRetry(() => import("./pages/MonParcours"));

// Mega Guide Page - Lazy load
const GuideJardinageVendee = lazyWithRetry(() => import("./pages/GuideJardinageVendee"));

// Coastal Residence Management Guide - Lazy load
const GuideGestionResidenceCotiere = lazyWithRetry(() => import("./pages/GuideGestionResidenceCotiere"));

// Robot Mower Finishing Service - Lazy load
const EntretienPelouseRobotTondeuse = lazyWithRetry(() => import("./pages/EntretienPelouseRobotTondeuse"));

// SEO niche expansion pages (single template, distinct URLs) - Lazy load
const NicheServicePage = lazyWithRetry(() => import("./pages/NicheServicePage"));

// English (/en/) SEO section - Lazy load
const EnglishHome = lazyWithRetry(() => import("./pages/en/EnglishHome"));
const EnglishServicePage = lazyWithRetry(() => import("./pages/en/EnglishServicePage"));
const EnglishContact = lazyWithRetry(() => import("./pages/en/EnglishContact"));
const EnglishAreasHub = lazyWithRetry(() => import("./pages/en/EnglishAreasHub"));

// Trust Positioning Page - Lazy load
const JardinierDeConfianceVendee = lazyWithRetry(() => import("./pages/JardinierDeConfianceVendee"));
const Normandie = lazyWithRetry(() => import("./pages/Normandie"));

// Gardening Advice Pages - Lazy load
const GardeningAdvicePage = lazyWithRetry(() => import("./pages/GardeningAdvicePage"));
const MonthlyAdvicePage = lazyWithRetry(() => import("./pages/MonthlyAdvicePage"));

// Seasonal Landing Pages - Lazy load
const SeasonalLandingPage = lazyWithRetry(() => import("./pages/SeasonalLandingPage"));

// Admin Pages - Lazy load (rarely accessed)
const AdminLogin = lazyWithRetry(() => import("./pages/AdminLogin"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboardNew"));
const AdminSettings = lazyWithRetry(() => import("./pages/AdminSettings"));
const AdminGallery = lazyWithRetry(() => import("./pages/AdminGallery"));
const AdminClients = lazyWithRetry(() => import("./pages/AdminClients"));
const AdminAuthCallback = lazyWithRetry(() => import("./pages/AdminAuthCallback"));

// Gallery / Réalisations Page - Lazy load
const Realisations = lazyWithRetry(() => import("./pages/Realisations"));

// 404 Page - Lazy load
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const FooterSwitch = () => {
  const { pathname } = useLocation();
  return isEnglishPath(pathname) ? <EnglishFooter /> : <Footer />;
};


function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AdminProvider>
            <CookieConsentProvider>
            <Routes>
              {/* Admin Routes (no header/footer) */}
              <Route path="/admin/login" element={
                <Suspense fallback={<PageLoader />}>
                  <AdminLogin />
                </Suspense>
              } />
              <Route 
                path="/admin/dashboard" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminDashboard />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/settings" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminSettings />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/galerie" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminGallery />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/clients" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminClients />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/auth/callback" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminAuthCallback />
                  </Suspense>
                } 
              />

              {/* Public Routes (with header/footer) */}
              <Route path="*" element={
                <LegacyRedirects>
                  <ScrollToTop />
                  <LangSync />
                  <SeasonalThemeOverlay />
                  <Header />
                  <MeteoConseilBanner />
                  <WhatsAppWidget />
                  <main className="pb-20 lg:pb-0">
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/services" element={<Services />} />
                      <Route path="/services/:serviceId" element={<ServiceDetail />} />
                      <Route path="/services/:serviceId/:ville" element={<ServiceLocationPage />} />
                      <Route path="/tarifs" element={<Tarifs />} />
                      <Route path="/devis" element={<Devis />} />
                      <Route path="/zone" element={<Zone />} />
                      <Route path="/zone/:ville" element={<LocationPage />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/parrainage" element={<Parrainage />} />
                      
                      {/* Long-tail SEO Pages */}
                      <Route path="/entretien-residence-secondaire" element={<EntretienResidenceSecondaire />} />
                      <Route path="/terrain-abandonne-vendee" element={<TerrainAbandonneVendee />} />
                      <Route path="/credit-impot-jardinage" element={<CreditImpotJardinage />} />
                      <Route path="/avance-immediate" element={<AvanceImmediatePage />} />
                      
                      {/* Problem-Solution Pages */}
                      <Route path="/jardin-en-friche" element={<JardinEnFriche />} />
                      <Route path="/haies-trop-hautes" element={<HaiesTropHautes />} />
                      
                      {/* About Page */}
                      <Route path="/mon-parcours" element={<MonParcours />} />

                      {/* Trust Positioning Page */}
                      <Route path="/jardinier-de-confiance-vendee" element={<JardinierDeConfianceVendee />} />

                      {/* Future Expansion — Normandy (launch 2027) */}
                      <Route path="/normandie" element={<Normandie />} />
                      
                      {/* Mega Guide Page */}
                      <Route path="/guide-jardinage-vendee" element={<GuideJardinageVendee />} />
                      
                      {/* Coastal Residence Management Guide */}
                      <Route path="/guide-gestion-residence-cotiere-vendee" element={<GuideGestionResidenceCotiere />} />

                      {/* Robot Mower Finishing Service */}
                      <Route path="/entretien-pelouse-robot-tondeuse" element={<EntretienPelouseRobotTondeuse />} />

                      {/* SEO niche expansion pages */}
                      <Route path="/jardin-pret-arrivee-vendee" element={<NicheServicePage slug="jardin-pret-arrivee-vendee" />} />
                      <Route path="/jardinage-senior-vendee" element={<NicheServicePage slug="jardinage-senior-vendee" />} />
                      <Route path="/entretien-massifs-desherbage-paillage" element={<NicheServicePage slug="entretien-massifs-desherbage-paillage" />} />
                      <Route path="/jardin-secheresse-paillage-vendee" element={<NicheServicePage slug="jardin-secheresse-paillage-vendee" />} />
                      <Route path="/scarification-entretien-pelouse-vendee" element={<NicheServicePage slug="scarification-entretien-pelouse-vendee" />} />

                      {/* English (/en/) SEO section */}
                      <Route path="/en" element={<EnglishHome />} />
                      <Route path="/en/" element={<EnglishHome />} />
                      <Route path="/en/garden-maintenance-vendee" element={<EnglishServicePage slug="garden-maintenance-vendee" />} />
                      <Route path="/en/holiday-home-garden-care-vendee" element={<EnglishServicePage slug="holiday-home-garden-care-vendee" />} />
                      <Route path="/en/campsite-garden-maintenance-vendee" element={<EnglishServicePage slug="campsite-garden-maintenance-vendee" />} />
                      <Route path="/en/garden-tidy-up-coex" element={<EnglishServicePage slug="garden-tidy-up-coex" />} />
                      <Route path="/en/lawn-mowing-coex" element={<EnglishServicePage slug="lawn-mowing-coex" />} />
                      <Route path="/en/hedge-cutting-coex" element={<EnglishServicePage slug="hedge-cutting-coex" />} />
                      <Route path="/en/pressure-washing-coex" element={<EnglishServicePage slug="pressure-washing-coex" />} />
                      <Route path="/en/weeding-garden-clearance-coex" element={<EnglishServicePage slug="weeding-garden-clearance-coex" />} />
                      <Route path="/en/sap-garden-tax-credit-france" element={<EnglishServicePage slug="sap-garden-tax-credit-france" />} />
                      <Route path="/en/contact" element={<EnglishContact />} />
                      {/* English area hub + area pages */}
                      <Route path="/en/areas" element={<EnglishAreasHub />} />
                      <Route path="/en/areas/coex" element={<EnglishServicePage slug="coex" area />} />
                      <Route path="/en/areas/landevieille-lac-du-jaunay" element={<EnglishServicePage slug="landevieille-lac-du-jaunay" area />} />
                      <Route path="/en/areas/la-chapelle-hermier" element={<EnglishServicePage slug="la-chapelle-hermier" area />} />
                      <Route path="/en/areas/saint-julien-des-landes" element={<EnglishServicePage slug="saint-julien-des-landes" area />} />
                      <Route path="/en/areas/apremont" element={<EnglishServicePage slug="apremont" area />} />

                      {/* Blog */}
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog-review" element={<BlogReview />} />
                      <Route path="/blog/:slug" element={<BlogArticle />} />
                      
                      {/* Gallery / Réalisations Page - disabled for now */}
                      {/* <Route path="/realisations" element={<Realisations />} /> */}
                      
                      {/* Legal Pages */}
                      <Route path="/mentions-legales" element={<MentionsLegales />} />
                      <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
                      <Route path="/cookies" element={<Cookies />} />
                      <Route path="/conditions-utilisation" element={<ConditionsUtilisation />} />
                      
                      {/* Gardening Advice Pages */}
                      {/* Monthly advice pages - must be before :slug route */}
                      <Route path="/conseils-jardin/janvier" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/fevrier" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/mars" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/avril" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/mai" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/juin" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/juillet" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/aout" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/septembre" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/octobre" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/novembre" element={<MonthlyAdvicePage />} />
                      <Route path="/conseils-jardin/decembre" element={<MonthlyAdvicePage />} />
                      {/* Topic-specific advice pages (catch-all) */}
                      <Route path="/conseils-jardin/:slug" element={<GardeningAdvicePage />} />

                      {/* Seasonal Landing Pages */}
                      <Route path="/entretien-jardin" element={<SeasonalLandingPage />} />
                      <Route path="/entretien-jardin/:season" element={<SeasonalLandingPage />} />
                      <Route path="/conseils-de-saison" element={<SeasonalLandingPage />} />
                      <Route path="/printemps" element={<SeasonalLandingPage />} />
                      <Route path="/ete" element={<SeasonalLandingPage />} />
                      <Route path="/automne" element={<SeasonalLandingPage />} />
                      <Route path="/hiver" element={<SeasonalLandingPage />} />

                      {/* 404 Page - Must be last */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
                </main>
                <FooterSwitch />
                <StickyMobileCTA />
              </LegacyRedirects>
            } />
          </Routes>
          <Toaster />
          </CookieConsentProvider>
          </AdminProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;